import React, { cloneElement, useState } from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import cx from "classnames";
import PropTypes from "prop-types";

import styles from "./Popover.module.scss";

const Popover = ({
  initialOpen = false,
  isOpen: controlledOpen,
  onOpenChange: setControlledOpen,
  render,
  button,
  onBlur,
  menuClassName,
  buttonClassName,
  menuPlacement,
  menuStyle,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (open) => {
      if (!open && onBlur) onBlur();

      setOpen(open);
    },
    placement: menuPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: 25, alignmentAxis: -8 }),
      flip({
        crossAxis: menuPlacement.includes("-"),
        fallbackAxisSideDirection: "end",
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const buttonWithHandler = cloneElement(button, {
    onClick: (e) => {
      if (button.props.onClick) {
        button.props.onClick(e); // Preserve existing click behavior
      }
    },
  });

  return (
    <>
      {buttonWithHandler && (
        <div
          className={cx(styles.button, buttonClassName)}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          {buttonWithHandler}
        </div>
      )}
      <FloatingPortal>
        {open && (
          <div
            className={cx(styles.menu, menuClassName)}
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...menuStyle }}
            {...getFloatingProps()}
          >
            {render && render}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

Popover.defaultProps = {
  menuText: "",
  onBlur: () => null,
  menuClassName: "",
  disabled: false,
  menuPlacement: "bottom-end",
};

Popover.propTypes = {
  menuText: PropTypes.string,
  onBlur: PropTypes.func,
  menuClassName: PropTypes.string,
  menuPlacement: PropTypes.string,
};

export default Popover;
